import { useState, useEffect, useCallback } from 'react';
import { supabase } from '~/services/supabase';

export interface IFormIntegrationConfig {
  /* HubSpot mappings */
  contactFieldMappings?: Array<{ formField: string; hubspotProperty: string }>;
  companyFieldMappings?: Array<{ formField: string; hubspotProperty: string }>;
  /* Attio mappings */
  personFieldMappings?: Array<{ formFields: string[]; attioAttribute: string }>;
  companyAttributeMappings?: Array<{ formFields: string[]; attioAttribute: string }>;
  /* Slack */
  channelId?: string;
  channelName?: string;
  /* Shared */
  syncPartialFills?: boolean;
  partialFillDelayMinutes?: number;
}

export interface IFormIntegrationRow {
  id: string;
  form_id: string;
  integration_id: string;
  enabled: boolean;
  config: IFormIntegrationConfig;
  created_at: string;
  updated_at: string;
}

interface UseFormIntegrationResult {
  formIntegration: IFormIntegrationRow | null;
  loading: boolean;
  error: string | null;
  save: (enabled: boolean, config: IFormIntegrationConfig) => Promise<void>;
  remove: () => Promise<void>;
}

export const useFormIntegration = (
  formId: string | undefined,
  integrationId: string | undefined,
): UseFormIntegrationResult => {
  const [formIntegration, setFormIntegration] = useState<IFormIntegrationRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!formId || !integrationId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetch = async () => {
      try {
        const { data, error: queryError } = await supabase
          .from('form_integrations')
          .select('*')
          .eq('form_id', formId)
          .eq('integration_id', integrationId)
          .maybeSingle();

        if (cancelled) return;
        if (queryError) throw queryError;
        setFormIntegration(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, [formId, integrationId]);

  const save = useCallback(
    async (enabled: boolean, config: IFormIntegrationConfig) => {
      if (!formId || !integrationId) return;
      setError(null);

      try {
        if (formIntegration) {
          // Update existing
          const { data, error: updateError } = await supabase
            .from('form_integrations')
            .update({ enabled, config })
            .eq('id', formIntegration.id)
            .select()
            .single();

          if (updateError) throw updateError;
          setFormIntegration(data);
        } else {
          // Insert new
          const { data, error: insertError } = await supabase
            .from('form_integrations')
            .insert({ form_id: formId, integration_id: integrationId, enabled, config })
            .select()
            .single();

          if (insertError) throw insertError;
          setFormIntegration(data);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save');
        throw err;
      }
    },
    [formId, integrationId, formIntegration],
  );

  const remove = useCallback(async () => {
    if (!formIntegration) return;
    setError(null);

    try {
      const { error: deleteError } = await supabase.from('form_integrations').delete().eq('id', formIntegration.id);

      if (deleteError) throw deleteError;
      setFormIntegration(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove');
      throw err;
    }
  }, [formIntegration]);

  return { formIntegration, loading, error, save, remove };
};
